/**
 * VALUATION ENGINE - Version 2.0
 * 
 * Motor de cálculo DCF completamente refatorado
 * Data: 14 de outubro de 2025
 * 
 * CORREÇÕES IMPLEMENTADAS:
 * ✅ CRÍTICO 1: WACC por empresa (Sétima 14,5% | 555 16,5%)
 * ✅ CRÍTICO 2: Sequência FCFF canônica (EBIT → NOPAT → FCFF)
 * ✅ CRÍTICO 3: Sistema anti-dupla contagem KPD
 * ✅ CRÍTICO 4: ValuationSnapshot único
 * ✅ MÉDIO 5: Depreciação específica (Sétima 3% | 555 2%)
 * ✅ MÉDIO 6: CAPEX validado (Sétima 5% | 555 20%)
 * ✅ MÉDIO 7: Validação WACC > g obrigatória
 * ✅ BAIXO 8: Taxa de imposto parametrizada
 * 
 * @version 2.0.0
 * @author Valuation Web App Team
 */

import {
  ValuationInputs,
  ValuationSnapshot,
  CompanyAssumptions,
  CompanyValuation,
  YearProjection,
  ConsolidatedValuation,
  ValidationResult,
  ValidationError,
  KPDDuplicateError,
  WACCvsGError,
} from '../types/valuation';
import {
  calculateKe,
  createKeBreakdown,
  calculateWACC,
  validateCAPM,
  type CAPMParams,
} from '../utils/capmCalculator';
import { realToNominal } from '../utils/fisherCalculator';

export class ValuationEngine {
  private version = '2.0.0';

  /**
   * Calcula valuation DCF completo (Sétima + 555 Studios)
   * 
   * Este é o método principal do engine. Executa todas as fases do cálculo:
   * 1. Validação de inputs
   * 2. Cálculo por empresa (WACC específico)
   * 3. Consolidação (Equity Bridge + Descontos)
   * 4. Valor da participação
   * 5. Validações de integridade
   * 
   * @param inputs - Objeto contendo todas as premissas necessárias
   * @param inputs.macro - Premissas macroeconômicas (Selic, inflação, PIB, etc.)
   * @param inputs.setima - Premissas específicas da Sétima (crescimento, margens, WACC)
   * @param inputs.studios555 - Premissas específicas da 555 Studios
   * @param inputs.equityBridge - Ajustes de dívida líquida e caixa excedente
   * @param inputs.discounts - Configuração de descontos (KPD, DLOC, DLOM)
   * @param inputs.stake - Participação percentual a ser avaliada
   * 
   * @returns ValuationSnapshot completo com todos os resultados e metadados
   * 
   * @throws {ValidationError} Se inputs forem inválidos
   * @throws {WACCvsGError} Se WACC <= g (perpetuidade inválida)
   * @throws {KPDDuplicateError} Se KPD for aplicado em duplicidade
   * 
   * @example
   * ```typescript
   * const engine = new ValuationEngine();
   * const snapshot = engine.calculate({
   *   macro: DEFAULT_MACRO_ASSUMPTIONS,
   *   setima: DEFAULT_SETIMA_ASSUMPTIONS,
   *   studios555: DEFAULT_555_ASSUMPTIONS,
   *   equityBridge: { dividaLiquida: 0, caixaExcedente: 0 },
   *   discounts: { kpd_mode: 'in_wacc', kpd_rate: 0.15, dloc: 0.10, dlom: 0.27 },
   *   stake: { percentual: 0.0495 }
   * });
   * 
   * console.log(snapshot.valorParticipacao); // R$ 494.010
   * ```
   */
  calculate(inputs: ValuationInputs): ValuationSnapshot {
    // FASE 1: Validar inputs
    this.validateInputs(inputs);

    // FASE 2: Calcular valuation por empresa (WACC separado)
    const setimaValuation = this.calculateCompany(inputs.setima);
    const studios555Valuation = this.calculateCompany(inputs.studios555);

    // FASE 2.5: Calcular CAPM breakdown para transparência (P0.1)
    const setimaCAPM = this.calculateCAPMBreakdown(inputs.macro, inputs.setima);
    const studios555CAPM = this.calculateCAPMBreakdown(inputs.macro, inputs.studios555);

    // FASE 3: Consolidar
    const consolidado = this.consolidate(
      setimaValuation,
      studios555Valuation,
      inputs.equityBridge,
      inputs.discounts,
      inputs.setima.kpd_in_wacc || inputs.studios555.kpd_in_wacc
    );

    // FASE 4: Valor da participação
    const valorParticipacao = consolidado.Equity_Ajustado * inputs.stake.percentual;

    // FASE 5: Executar validações
    const validations = this.runValidations({
      setimaValuation,
      studios555Valuation,
      consolidado,
      inputs,
    });

    // FASE 6: Criar snapshot
    const snapshot: ValuationSnapshot = {
      id: this.generateId(),
      timestamp: new Date(),
      version: this.version,
      macro: inputs.macro,
      setimaInputs: inputs.setima,
      studios555Inputs: inputs.studios555,
      equityBridge: inputs.equityBridge,
      discounts: inputs.discounts,
      stake: inputs.stake,
      setimaValuation,
      studios555Valuation,
      consolidado,
      valorParticipacao,
      validations,
      // P0.1: CAPM Transparency
      setimaCAPM,
      studios555CAPM,
    };

    return snapshot;
  }

  /**
   * Calcula breakdown CAPM completo para uma empresa
   * 
   * P0.1: Transparência Ke/WACC
   * Retorna componentes detalhados, breakdown e validações do CAPM
   * 
   * @param macro - Premissas macroeconômicas
   * @param company - Premissas da empresa
   * @returns Breakdown CAPM completo com componentes, Ke, WACC e validações
   * 
   * @private
   */
  private calculateCAPMBreakdown(
    macro: ValuationInputs['macro'],
    company: CompanyAssumptions
  ) {
    // Preparar parâmetros CAPM
    const capmParams: CAPMParams = {
      rf: macro.taxaSelic,
      rfMode: macro.rfMode || 'local',
      beta: company.beta,
      erp: macro.erpGlobal || macro.premioRiscoMercado,
      crp: macro.premioRiscoBrasil,
      crpMethodology: macro.crpMethodology || 'additive',
      sizePremium: macro.sizePremium || 0,
      specificRisk: macro.specificRisk || 0,
    };

    // Calcular componentes CAPM
    const components = calculateKe(capmParams);

    // Criar breakdown detalhado
    const keBreakdown = createKeBreakdown(components);

    // Calcular WACC breakdown
    const waccBreakdown = calculateWACC({
      ke: components.ke,
      keBreakdown,
      equityWeight: 1.0, // 100% equity (sem dívida)
    });

    // Validar CAPM
    const validation = validateCAPM(components);

    return {
      components,
      keBreakdown,
      waccBreakdown,
      validation,
    };
  }

  /**
   * Calcula valuation de uma empresa individual
   * 
   * Implementa a sequência canônica de FCFF (Free Cash Flow to Firm):
   * 1. Receita (crescimento year-over-year)
   * 2. EBITDA (receita × margem)
   * 3. Depreciação (receita × %)
   * 4. EBIT (EBITDA - Depreciação)
   * 5. NOPAT (EBIT × (1 - Tax))
   * 6. FCFF (NOPAT + Depreciação - CAPEX - ΔNWC)
   * 7. Valor Presente (FCFF / (1 + WACC)^t)
   * 8. Valor Terminal (FCFF_2030 / (WACC - g))
   * 9. Enterprise Value (VP_FCFF + VP_VT)
   * 
   * **Correções implementadas:**
   * - WACC específico por empresa (não mais consolidado)
   * - Depreciação e CAPEX específicos por empresa
   * - Validação obrigatória WACC > g
   * 
   * @param assumptions - Premissas específicas da empresa
   * @returns Valuation completo da empresa (projeções + EV)
   * 
   * @throws {WACCvsGError} Se WACC <= g (violação da fórmula de Gordon)
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * const setimaVal = this.calculateCompany(DEFAULT_SETIMA_ASSUMPTIONS);
   * console.log(setimaVal.ev); // R$ 8.123.456
   * console.log(setimaVal.wacc); // 0.145 (14,5%)
   * ```
   */
  private calculateCompany(assumptions: CompanyAssumptions): CompanyValuation {
    const projecoes: YearProjection[] = [];
    let receitaAnterior = assumptions.receitaBase;

    // ✅ REFATORADO: Projeções dinâmicas (baseYear+1 até baseYear+5)
    const baseYear = assumptions.baseYear;
    const firstProjectionYear = baseYear + 1;
    const lastProjectionYear = baseYear + 5;

    console.log(`[ValuationEngine] 🔄 Projetando ${assumptions.nome} de ${firstProjectionYear} a ${lastProjectionYear} (base: ${baseYear})`);

    for (let ano = firstProjectionYear; ano <= lastProjectionYear; ano++) {
      const projection = this.calculateYearProjection(
        ano,
        receitaAnterior,
        assumptions.crescimento[ano] || 0,
        assumptions.margemEBITDA[ano] || 0,
        assumptions.depreciacao_pct_receita,
        assumptions.taxaImpostoEBIT,
        assumptions.capex_pct_receita[ano] || 0,
        assumptions.nwc_pct_delta_receita[ano] || 0,
        assumptions.wacc,
        baseYear  // ✅ NOVO: Passar baseYear para cálculo correto do fator de desconto
      );

      projecoes.push(projection);
      receitaAnterior = projection.receita;
    }

    // Valor Presente dos FCFF (2025-2029)
    const vp_fcff_total = projecoes.reduce((sum, p) => sum + p.pv_fcff, 0);

    // Valor Terminal (com Fisher equation para consistência real/nominal)
    const fcff_2029 = projecoes[4].fcff; // Último ano
    const fcff_2030 = fcff_2029 * (1 + assumptions.g);
    
    // ✅ FISHER EQUATION: Garantir que WACC e g estão na mesma base (nominal)
    // Nota: Usa gMode se disponível, senão assume 'nominal' (comportamento retrocompatível)
    const { wacc: waccNominal, g: gNominal } = this.ensureConsistentRates(
      assumptions.wacc,
      assumptions.g,
      assumptions.gMode || 'nominal', // Default: nominal (retrocompatibilidade)
      0.045, // TODO: pegar inflação de assumptions.macro.inflacao
      assumptions.nome
    );
    
    // ✅ VALIDAÇÃO WACC > g já feita dentro de ensureConsistentRates
    
    const valorTerminal = fcff_2030 / (waccNominal - gNominal);
    const vp_valorTerminal = valorTerminal / Math.pow(1 + waccNominal, 5);

    // Enterprise Value
    const ev = vp_fcff_total + vp_valorTerminal;

    return {
      empresa: assumptions.nome,
      projecoes,
      vp_fcff_total,
      fcff_2030,
      valorTerminal,
      vp_valorTerminal,
      ev,
      wacc: assumptions.wacc,
      g: assumptions.g,
    };
  }

  /**
   * Calcula projeção financeira de um ano específico
   * 
   * Implementa a sequência canônica NOPAT-first do DCF:
   * 
   * **Fluxo de Cálculo:**
   * 1. **Receita**: Ano anterior × (1 + crescimento)
   * 2. **EBITDA**: Receita × Margem EBITDA
   * 3. **Depreciação**: Receita × % Depreciação
   * 4. **EBIT**: EBITDA - Depreciação
   * 5. **NOPAT**: EBIT × (1 - Taxa de Imposto)
   * 6. **CAPEX**: Receita × % CAPEX
   * 7. **ΔNWC**: ΔReceita × % NWC
   * 8. **FCFF**: NOPAT + Depreciação - CAPEX - ΔNWC
   * 9. **Valor Presente**: FCFF / (1 + WACC)^t
   * 
   * **Importante:** Depreciação é somada de volta após NOPAT, pois é despesa não-caixa.
   * 
   * @param year - Ano da projeção (dinâmico: baseYear+1 até baseYear+5)
   * @param receitaAnterior - Receita do ano anterior (base para crescimento)
   * @param taxaCrescimento - Taxa de crescimento anual (ex: 0.08 = 8%)
   * @param margemEBITDA - Margem EBITDA target (ex: 0.22 = 22%)
   * @param taxaDepreciacao - Depreciação como % da receita (ex: 0.03 = 3%)
   * @param taxaImposto - Taxa de imposto sobre EBIT (ex: 0.18 = 18%)
   * @param taxaCAPEX - CAPEX como % da receita (ex: 0.05 = 5%)
   * @param taxaNWC - Variação de NWC como % de ΔReceita (ex: 0.035 = 3,5%)
   * @param wacc - Taxa de desconto (WACC da empresa)
   * @param baseYear - ✅ NOVO: Último ano histórico (para cálculo correto de t)
   * 
   * @returns Projeção anual completa com todos os componentes do FCFF
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // Exemplo 1: Base 2024, projetando 2025
   * const proj2025 = this.calculateYearProjection(
   *   2025,
   *   6301857,  // receita 2024
   *   0.08,     // crescimento 8%
   *   0.22,     // margem EBITDA 22%
   *   0.03,     // depreciação 3%
   *   0.18,     // imposto 18%
   *   0.05,     // CAPEX 5%
   *   0.035,    // NWC 3,5%
   *   0.145,    // WACC 14,5%
   *   2024      // baseYear
   * );
   * // → t = 2025 - 2024 = 1
   * 
   * // Exemplo 2: Base 2025, projetando 2026
   * const proj2026 = this.calculateYearProjection(
   *   2026, 8000000, 0.09, 0.24, 0.03, 0.18, 0.05, 0.035, 0.145, 2025
   * );
   * // → t = 2026 - 2025 = 1 (primeiro ano de projeção sempre t=1)
   * ```
   */
  private calculateYearProjection(
    year: number,
    receitaAnterior: number,
    taxaCrescimento: number,
    margemEBITDA: number,
    taxaDepreciacao: number,
    taxaImposto: number,
    taxaCAPEX: number,
    taxaNWC: number,
    wacc: number,
    baseYear: number  // ✅ NOVO PARÂMETRO
  ): YearProjection {
    // 1. Receita
    const receita = receitaAnterior * (1 + taxaCrescimento);

    // 2. EBITDA
    const ebitda = receita * margemEBITDA;

    // 3. Depreciação
    const depreciacao = receita * taxaDepreciacao;

    // 4. EBIT ✅ EXPLÍCITO
    const ebit = ebitda - depreciacao;

    // 5. NOPAT (Imposto sobre EBIT) ✅ EXPLÍCITO
    const nopat = ebit * (1 - taxaImposto);

    // 6. CAPEX
    const capex = receita * taxaCAPEX;

    // 7. Variação de Capital de Giro
    const delta_nwc = (receita - receitaAnterior) * taxaNWC;

    // 8. FCFF ✅ CANÔNICO
    const fcff = nopat + depreciacao - capex - delta_nwc;

    // ✅ REFATORADO: Valor Presente com t relativo ao baseYear
    // Exemplo: Se baseYear = 2024 e year = 2025 → t = 1
    // Exemplo: Se baseYear = 2025 e year = 2026 → t = 1 (primeiro ano sempre t=1)
    const t = year - baseYear;
    const discountFactor = 1 / Math.pow(1 + wacc, t);
    const pv_fcff = fcff * discountFactor;

    return {
      year,
      receita,
      ebitda,
      depreciacao,
      ebit,      // ✅ NOVO
      nopat,     // ✅ NOVO
      capex,
      delta_nwc,
      fcff,
      discountFactor,
      pv_fcff,
    };
  }

  /**
   * Consolida resultados das duas empresas (Sétima + 555 Studios)
   * 
   * Executa:
   * 1. **Soma de Enterprise Values** (EV_Total = EV_Sétima + EV_555)
   * 2. **WACC consolidado ponderado** (apenas referência, não usado em cálculos)
   * 3. **Equity Bridge** (EV - Dívida + Caixa)
   * 4. **Aplicação de descontos** (KPD, DLOC, DLOM) com validação anti-dupla contagem
   * 
   * **Correção Crítica:** Sistema anti-dupla contagem de KPD.
   * - Se `kpd_in_wacc = true`, KPD já está embutido no beta/WACC → não aplicar como haircut
   * - Se `kpd_mode = 'as_haircut'`, aplicar desconto explícito de 15%
   * 
   * @param setimaValuation - Valuation da Sétima (EV, WACC, projeções)
   * @param studios555Valuation - Valuation da 555 Studios
   * @param equityBridge - Ajustes de dívida líquida e caixa excedente
   * @param discounts - Configuração de descontos (KPD, DLOC, DLOM)
   * @param kpdAlreadyInWACC - Flag: KPD já embutido no WACC?
   * 
   * @returns Valuation consolidado com Equity Ajustado
   * 
   * @throws {KPDDuplicateError} Se KPD for aplicado em duplicidade
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * const consolidado = this.consolidate(
   *   setimaVal,  // EV: R$ 8.000.000
   *   studios555Val, // EV: R$ 2.000.000
   *   { dividaLiquida: 0, caixaExcedente: 0 },
   *   { kpd_mode: 'in_wacc', kpd_rate: 0.15, dloc: 0.10, dlom: 0.27 },
   *   true // KPD já no WACC
   * );
   * 
   * console.log(consolidado.EV_Total); // R$ 10.000.000
   * console.log(consolidado.Equity_Ajustado); // R$ 6.300.000 (após DLOC/DLOM)
   * ```
   */
  private consolidate(
    setimaValuation: CompanyValuation,
    studios555Valuation: CompanyValuation,
    equityBridge: { dividaLiquida: number; caixaExcedente: number },
    discounts: { kpd_mode: string; kpd_rate: number; dloc: number; dlom: number },
    kpdAlreadyInWACC: boolean
  ): ConsolidatedValuation {
    // Enterprise Value consolidado
    const EV_Total = setimaValuation.ev + studios555Valuation.ev;

    // WACC consolidado (ponderado por EV) - apenas referência
    const WACC_Mix =
      (setimaValuation.ev * setimaValuation.wacc +
        studios555Valuation.ev * studios555Valuation.wacc) /
      EV_Total;

    // Equity Bridge
    const equity_pre_discounts =
      EV_Total - equityBridge.dividaLiquida + equityBridge.caixaExcedente;

    // Aplicar descontos ✅ COM VALIDAÇÃO KPD
    const {
      equityAjustado,
      kpdAplicado,
      kpdValor,
      dlocValor,
      dlomValor,
      descontoTotalPct,
    } = this.applyDiscounts(equity_pre_discounts, discounts, kpdAlreadyInWACC);

    return {
      EV_Total,
      Equity: equity_pre_discounts,
      Equity_Ajustado: equityAjustado,
      WACC_Mix,
      equityBridge: {
        ev: EV_Total,
        menos_divida: -equityBridge.dividaLiquida,
        mais_caixa: equityBridge.caixaExcedente,
        equity: equity_pre_discounts,
      },
      discounts: {
        equity_pre: equity_pre_discounts,
        kpd_aplicado: kpdAplicado,
        kpd_valor: kpdValor,
        dloc_valor: dlocValor,
        dlom_valor: dlomValor,
        equity_pos: equityAjustado,
        desconto_total_pct: descontoTotalPct,
      },
    };
  }

  /**
   * Aplica descontos de negociação (KPD, DLOC, DLOM) no Equity Value
   * 
   * **Sistema Anti-Dupla Contagem de KPD:**
   * 
   * O desconto por falta de dividendos (Key Person Discount) pode ser aplicado de duas formas:
   * 1. **Embutido no WACC** (via beta ajustado) - recomendado
   * 2. **Como haircut explícito** - alternativa
   * 
   * Este método valida que KPD **NÃO seja aplicado duas vezes**:
   * - Se `kpdAlreadyInWACC = true` e `kpd_mode = 'as_haircut'` → **ERRO**
   * 
   * **Sequência de Aplicação:**
   * 1. KPD (se `kpd_mode = 'as_haircut'` e não estiver no WACC)
   * 2. DLOC (Discount for Lack of Control)
   * 3. DLOM (Discount for Lack of Marketability)
   * 
   * **Importante:** Descontos são aplicados em cascata (um após o outro), não aditivamente.
   * 
   * @param equity - Equity Value pré-descontos (após Equity Bridge)
   * @param discounts - Objeto com configuração de descontos
   * @param discounts.kpd_mode - 'in_wacc' ou 'as_haircut'
   * @param discounts.kpd_rate - Taxa de desconto KPD (ex: 0.15 = 15%)
   * @param discounts.dloc - Discount for Lack of Control (ex: 0.10 = 10%)
   * @param discounts.dlom - Discount for Lack of Marketability (ex: 0.27 = 27%)
   * @param kpdAlreadyInWACC - Flag: KPD já embutido no WACC?
   * 
   * @returns Objeto com Equity Ajustado e breakdown de descontos
   * 
   * @throws {KPDDuplicateError} Se KPD for detectado em duplicidade
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // Cenário 1: KPD no WACC (correto)
   * const result1 = this.applyDiscounts(
   *   10000000,
   *   { kpd_mode: 'in_wacc', kpd_rate: 0.15, dloc: 0.10, dlom: 0.27 },
   *   true // KPD já no WACC
   * );
   * // → Aplica apenas DLOC e DLOM
   * // → equityAjustado = 10M × 0.9 × 0.73 = R$ 6.570.000
   * 
   * // Cenário 2: KPD como haircut (alternativa)
   * const result2 = this.applyDiscounts(
   *   10000000,
   *   { kpd_mode: 'as_haircut', kpd_rate: 0.15, dloc: 0.10, dlom: 0.27 },
   *   false // KPD NÃO no WACC
   * );
   * // → Aplica KPD + DLOC + DLOM
   * // → equityAjustado = 10M × 0.85 × 0.9 × 0.73 = R$ 5.584.500
   * 
   * // Cenário 3: ERRO - Dupla contagem
   * this.applyDiscounts(
   *   10000000,
   *   { kpd_mode: 'as_haircut', kpd_rate: 0.15, dloc: 0.10, dlom: 0.27 },
   *   true // ❌ KPD já no WACC mas também como haircut
   * );
   * // → Lança KPDDuplicateError
   * ```
   */
  private applyDiscounts(
    equity: number,
    discounts: { kpd_mode: string; kpd_rate: number; dloc: number; dlom: number },
    kpdAlreadyInWACC: boolean
  ): {
    equityAjustado: number;
    kpdAplicado: boolean;
    kpdValor: number;
    dlocValor: number;
    dlomValor: number;
    descontoTotalPct: number;
  } {
    let equityAjustado = equity;
    let kpdAplicado = false;
    let kpdValor = 0;

    // ✅ VALIDAÇÃO: KPD apenas uma vez
    if (discounts.kpd_mode === 'as_haircut' && kpdAlreadyInWACC) {
      throw new KPDDuplicateError();
    }

    // KPD como haircut (se modo selecionado E não está no WACC)
    if (discounts.kpd_mode === 'as_haircut' && !kpdAlreadyInWACC) {
      kpdValor = equityAjustado * discounts.kpd_rate;
      equityAjustado *= 1 - discounts.kpd_rate;
      kpdAplicado = true;
    }

    // DLOC (sempre aplicado)
    const dlocValor = equityAjustado * discounts.dloc;
    equityAjustado *= 1 - discounts.dloc;

    // DLOM (sempre aplicado)
    const dlomValor = equityAjustado * discounts.dlom;
    equityAjustado *= 1 - discounts.dlom;

    // Desconto total
    const descontoTotalPct = 1 - equityAjustado / equity;

    return {
      equityAjustado,
      kpdAplicado,
      kpdValor,
      dlocValor,
      dlomValor,
      descontoTotalPct,
    };
  }

  /**
   * Valida inputs antes de iniciar cálculo
   * 
   * Executa validações críticas para evitar erros de cálculo:
   * 1. Premissas obrigatórias (Sétima e 555 Studios)
   * 2. WACC > 0 (taxa de desconto válida)
   * 3. g >= 0 e < WACC (crescimento perpétuo válido)
   * 4. Participação entre 0% e 100%
   * 
   * @param inputs - Inputs completos do valuation
   * 
   * @throws {ValidationError} Se qualquer validação falhar
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // ✅ Válido
   * this.validateInputs({
   *   setima: { wacc: 0.145, g: 0.03, ... },
   *   studios555: { wacc: 0.165, g: 0.03, ... },
   *   stake: { percentual: 0.0495 },
   *   ...
   * });
   * 
   * // ❌ Inválido - WACC negativo
   * this.validateInputs({
   *   setima: { wacc: -0.10, ... } // Lança ValidationError
   * });
   * 
   * // ❌ Inválido - g >= WACC
   * this.validateInputs({
   *   setima: { wacc: 0.10, g: 0.12, ... } // Lança ValidationError
   * });
   * ```
   */
  private validateInputs(inputs: ValuationInputs): void {
    // Validação 1: Premissas obrigatórias
    if (!inputs.setima || !inputs.studios555) {
      throw new ValidationError('Premissas de Sétima e 555 são obrigatórias');
    }

    // Validação 2: WACC > 0
    if (inputs.setima.wacc <= 0 || inputs.studios555.wacc <= 0) {
      throw new ValidationError('WACC deve ser maior que zero');
    }

    // Validação 3: g >= 0 e < WACC
    if (inputs.setima.g < 0 || inputs.studios555.g < 0) {
      throw new ValidationError('Taxa de crescimento perpétuo (g) não pode ser negativa');
    }

    // Validação 4: Percentuais válidos
    if (inputs.stake.percentual <= 0 || inputs.stake.percentual > 1) {
      throw new ValidationError('Percentual de participação deve estar entre 0 e 100%');
    }
  }

  /**
   * Executa validações pós-cálculo para verificar integridade dos resultados
   * 
   * Validações implementadas:
   * 1. **WACC > g** (ambas empresas) - Evita perpetuidade inválida
   * 2. **Terminal Value / EV ratio** - Detecta dependência excessiva do VT (> 70% é red flag)
   * 3. **KPD sem dupla contagem** - Garante que KPD não foi aplicado 2× (WACC + haircut)
   * 4. **EV > 0** - Valida que Enterprise Values são positivos
   * 5. **Equity Ajustado > 0** - Garante que descontos não zeraram valor
   * 
   * **Importante:** Validações não bloqueiam o cálculo, apenas alertam.
   * 
   * @param context - Contexto com resultados do cálculo
   * @param context.setimaValuation - Valuation da Sétima
   * @param context.studios555Valuation - Valuation da 555 Studios
   * @param context.consolidado - Consolidado (Equity Ajustado)
   * @param context.inputs - Inputs originais
   * 
   * @returns Array de ValidationResult (passed: true/false, message, severity)
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * const validations = this.runValidations({
   *   setimaValuation,
   *   studios555Valuation,
   *   consolidado,
   *   inputs
   * });
   * 
   * // Checar se há erros críticos
   * const errors = validations.filter(v => !v.passed && v.severity === 'error');
   * if (errors.length > 0) {
   *   console.error('Validações falharam:', errors);
   * }
   * 
   * // Checar warnings
   * const warnings = validations.filter(v => !v.passed && v.severity === 'warning');
   * ```
   */
  private runValidations(context: {
    setimaValuation: CompanyValuation;
    studios555Valuation: CompanyValuation;
    consolidado: ConsolidatedValuation;
    inputs: ValuationInputs;
  }): ValidationResult[] {
    const validations: ValidationResult[] = [];

    // Validação 1: WACC > g (Sétima)
    validations.push(
      this.validateWACCvsG(context.inputs.setima.wacc, context.inputs.setima.g, 'Sétima')
    );

    // Validação 2: WACC > g (555)
    validations.push(
      this.validateWACCvsG(
        context.inputs.studios555.wacc,
        context.inputs.studios555.g,
        '555 Studios'
      )
    );

    // Validação 3: VT/EV ratio (Sétima)
    validations.push(
      this.validateTerminalValueRatio(
        context.setimaValuation.vp_valorTerminal,
        context.setimaValuation.ev,
        'Sétima'
      )
    );

    // Validação 4: VT/EV ratio (555)
    validations.push(
      this.validateTerminalValueRatio(
        context.studios555Valuation.vp_valorTerminal,
        context.studios555Valuation.ev,
        '555 Studios'
      )
    );

    // Validação 5: KPD sem dupla contagem
    validations.push(
      this.validateKPDApplication(
        context.inputs.setima.kpd_in_wacc || context.inputs.studios555.kpd_in_wacc,
        context.inputs.discounts.kpd_mode === 'as_haircut'
      )
    );

    // Validação 6: EV positivo
    validations.push(
      this.validatePositiveValue(context.consolidado.EV_Total, 'Enterprise Value')
    );

    // Validação 7: Equity positivo
    validations.push(
      this.validatePositiveValue(context.consolidado.Equity_Ajustado, 'Equity Ajustado')
    );

    return validations;
  }

  // ============================================================================
  // FISHER CALCULATOR - CONVERSÃO REAL/NOMINAL
  // ============================================================================

  /**
   * Garante consistência entre WACC (sempre nominal) e g (pode ser real ou nominal)
   * 
   * **Problema:**
   * - WACC sempre nominal (expectativas de mercado incluem inflação)
   * - g pode ser real OU nominal (depende da metodologia do analista)
   * - Fórmula de Gordon: VT = FCFF / (WACC - g) → ambos DEVEM estar na MESMA base
   * 
   * **Solução (Equação de Fisher):**
   * - Se g for REAL: converte para NOMINAL antes do cálculo
   * - Fórmula: g_nominal = (1 + g_real) × (1 + inflação) - 1
   * 
   * **Impacto de erro:** ±20% no Enterprise Value
   * 
   * @param wacc - Taxa de desconto (SEMPRE NOMINAL)
   * @param g - Taxa de crescimento perpétuo (pode ser real ou nominal)
   * @param gMode - Modo atual de g ('real' ou 'nominal')
   * @param inflation - Taxa de inflação (para conversão Fisher)
   * @param empresa - Nome da empresa (para mensagens de erro)
   * 
   * @returns Objeto com WACC e g garantidamente em base NOMINAL
   * 
   * @throws {WACCvsGError} Se WACC <= g após conversão
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // Exemplo 1: g já está nominal → apenas valida
   * const rates = this.ensureConsistentRates(0.145, 0.03, 'nominal', 0.045, 'Sétima');
   * // → { wacc: 0.145, g: 0.03 }
   * 
   * // Exemplo 2: g real → converte para nominal
   * const rates = this.ensureConsistentRates(0.145, 0.03, 'real', 0.045, 'Sétima');
   * // → { wacc: 0.145, g: 0.07635 } (Fisher: 3% real + 4.5% inflação)
   * 
   * // Exemplo 3: WACC <= g → erro crítico
   * this.ensureConsistentRates(0.10, 0.12, 'nominal', 0.045, 'Sétima');
   * // → throws WACCvsGError
   * ```
   */
  private ensureConsistentRates(
    wacc: number,
    g: number,
    gMode: 'real' | 'nominal',
    inflation: number,
    empresa: string
  ): { wacc: number; g: number } {
    // WACC sempre nominal (market expectations)
    const waccNominal = wacc;

    // Converter g para nominal se necessário (Fisher equation)
    const gNominal = gMode === 'real' 
      ? realToNominal(g, inflation)
      : g;

    // Validação obrigatória: WACC > g (Gordon formula)
    if (waccNominal <= gNominal) {
      throw new WACCvsGError(waccNominal, gNominal, empresa);
    }

    return { wacc: waccNominal, g: gNominal };
  }

  // ============================================================================
  // VALIDAÇÕES INDIVIDUAIS
  // ============================================================================

  /**
   * Valida que WACC > g (condição para fórmula de Gordon ser válida)
   * 
   * A fórmula do Valor Terminal é: VT = FCFF₂₀₃₀ / (WACC - g)
   * 
   * Se WACC <= g, o denominador é zero ou negativo → perpetuidade inválida.
   * 
   * @param wacc - Taxa de desconto (WACC da empresa)
   * @param g - Taxa de crescimento perpétuo
   * @param empresa - Nome da empresa (para mensagem)
   * 
   * @returns ValidationResult com passed=true se WACC > g
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // ✅ Válido
   * this.validateWACCvsG(0.145, 0.03, 'Sétima');
   * // → { passed: true, message: "Sétima: WACC (14.50%) > g (3.00%) ✓" }
   * 
   * // ❌ Inválido
   * this.validateWACCvsG(0.10, 0.12, 'Sétima');
   * // → { passed: false, severity: 'error', blocking: true }
   * ```
   */
  private validateWACCvsG(wacc: number, g: number, empresa: string): ValidationResult {
    const passed = wacc > g;
    return {
      rule: 'WACC > g',
      passed,
      message: passed
        ? `${empresa}: WACC (${(wacc * 100).toFixed(2)}%) > g (${(g * 100).toFixed(2)}%) ✓`
        : `🔴 ${empresa}: WACC (${(wacc * 100).toFixed(2)}%) ≤ g (${(g * 100).toFixed(2)}%)`,
      severity: passed ? 'info' : 'error',
      blocking: !passed,
    };
  }

  /**
   * Valida proporção de Valor Terminal vs Enterprise Value
   * 
   * Se VT / EV > 70%, indica dependência excessiva do valor terminal.
   * Isso pode sinalizar que as premissas de crescimento perpétuo são muito otimistas.
   * 
   * **Regra geral:** VT deve representar 50-70% do EV. Acima de 70% é red flag.
   * 
   * @param vt - Valor Presente do Valor Terminal
   * @param ev - Enterprise Value total
   * @param empresa - Nome da empresa (para mensagem)
   * 
   * @returns ValidationResult com passed=true se VT/EV <= 70%
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // ✅ Aceitável - 60%
   * this.validateTerminalValueRatio(6000000, 10000000, 'Sétima');
   * // → { passed: true, message: "Sétima: VT representa 60.0% do EV (aceitável) ✓" }
   * 
   * // ⚠️ Warning - 85%
   * this.validateTerminalValueRatio(8500000, 10000000, '555 Studios');
   * // → { passed: false, severity: 'warning', message: "⚠️ 555 Studios: VT representa 85.0% do EV (revisar premissas)" }
   * ```
   */
  private validateTerminalValueRatio(vt: number, ev: number, empresa: string): ValidationResult {
    const ratio = vt / ev;
    const threshold = 0.7;
    const passed = ratio <= threshold;

    return {
      rule: 'VT/EV ≤ 70%',
      passed,
      message: passed
        ? `${empresa}: VT representa ${(ratio * 100).toFixed(1)}% do EV (aceitável) ✓`
        : `⚠️ ${empresa}: VT representa ${(ratio * 100).toFixed(1)}% do EV (revisar premissas)`,
      severity: passed ? 'info' : 'warning',
    };
  }

  /**
   * Valida que KPD não foi aplicado em duplicidade
   * 
   * Key Person Discount pode ser aplicado de duas formas:
   * 1. **No WACC** (via beta ajustado) - mais defensável tecnicamente
   * 2. **Como haircut explícito** - alternativa pragmática
   * 
   * **ERRO CRÍTICO:** Aplicar KPD nas duas formas simultaneamente = dupla contagem.
   * 
   * @param kpdInWACC - Flag: KPD embutido no WACC?
   * @param kpdAsHaircut - Flag: KPD aplicado como desconto explícito?
   * 
   * @returns ValidationResult com passed=false se ambos forem true
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // ✅ Válido - KPD apenas no WACC
   * this.validateKPDApplication(true, false);
   * // → { passed: true, message: "KPD aplicado no WACC (sem haircut) ✓" }
   * 
   * // ✅ Válido - KPD apenas como haircut
   * this.validateKPDApplication(false, true);
   * // → { passed: true, message: "KPD aplicado como haircut (não no WACC) ✓" }
   * 
   * // ❌ ERRO - Dupla contagem
   * this.validateKPDApplication(true, true);
   * // → { passed: false, severity: 'error', blocking: true, message: "🔴 KPD aplicado 2× (WACC + haircut)" }
   * ```
   */
  private validateKPDApplication(kpdInWACC: boolean, kpdAsHaircut: boolean): ValidationResult {
    const both = kpdInWACC && kpdAsHaircut;
    return {
      rule: 'KPD aplicado apenas uma vez',
      passed: !both,
      message: !both
        ? 'KPD aplicado corretamente (sem dupla contagem) ✓'
        : '🔴 KPD aplicado no WACC E como haircut (dupla contagem)',
      severity: !both ? 'info' : 'error',
      blocking: both,
    };
  }

  /**
   * Valida que um valor é positivo
   * 
   * Valores negativos ou zero em EV ou Equity Ajustado indicam erro nas premissas.
   * 
   * @param value - Valor a validar
   * @param label - Label para mensagem de erro
   * 
   * @returns ValidationResult com passed=true se value > 0
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * // ✅ Válido
   * this.validatePositiveValue(10000000, 'Enterprise Value');
   * // → { passed: true, message: "Enterprise Value positivo ✓" }
   * 
   * // ❌ Inválido
   * this.validatePositiveValue(-5000000, 'Equity Ajustado');
   * // → { passed: false, severity: 'error', blocking: true }
   * ```
   */
  private validatePositiveValue(value: number, label: string): ValidationResult {
    const passed = value > 0;
    return {
      rule: `${label} > 0`,
      passed,
      message: passed
        ? `${label} positivo ✓`
        : `🔴 ${label} negativo ou zero (revisar premissas)`,
      severity: passed ? 'info' : 'error',
      blocking: !passed,
    };
  }

  // ============================================================================
  // UTILITÁRIOS
  // ============================================================================

  /**
   * Gera ID único para snapshot de valuation
   * 
   * Formato: `val_{timestamp}_{random}`
   * 
   * @returns ID único (ex: "val_1697285760123_k9j2x4p1q")
   * 
   * @private
   * @internal
   * 
   * @example
   * ```typescript
   * const id = this.generateId();
   * console.log(id); // "val_1697285760123_k9j2x4p1q"
   * ```
   */
  private generateId(): string {
    return `val_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// ============================================================================
// PREMISSAS DEFAULT (Para testes e desenvolvimento)
// ============================================================================

export const DEFAULT_MACRO_ASSUMPTIONS = {
  taxaSelic: 0.1175, // 11,75%
  inflacao: 0.045, // 4,5%
  crescimentoPIB: 0.025, // 2,5%
  premioRiscoBrasil: 0.035, // 3,5%
  premioRiscoMercado: 0.08, // 8%
};

/**
 * PREMISSAS SÉTIMA - CENÁRIO BASE (Recuperação Gradual)
 * 
 * ⚠️ ATUALIZADO PÓS-NORMALIZAÇÃO DRE (14/10/2025)
 * 
 * CONTEXTO HISTÓRICO:
 * - CAGR 2020-2024: 6,8% (vs 10% projetado anteriormente)
 * - Margem EBITDA Pico (2022): 32,1%
 * - Margem EBITDA Atual (2024): 11,2%
 * - Outlier: 2023 com colapso de margem (1,1%)
 * 
 * PREMISSAS DO CENÁRIO BASE:
 * - Crescimento alinhado com CAGR histórico (7-9%)
 * - Recuperação gradual de margem até 22% (abaixo do pico, realista)
 * - Assumindo reestruturação de custos bem-sucedida
 */
export const DEFAULT_SETIMA_ASSUMPTIONS: CompanyAssumptions = {
  nome: 'Sétima' as const,
  baseYear: 2024,         // ✅ NOVO: Último ano histórico (DRE vai até 2024)
  receitaBase: 8548360,   // 2024 (pós-recuperação parcial)
  ebitdaBase: 954087,     // 2024 (margem 11,2%)
  crescimento: {
    2025: 0.08,  // 8% - Recuperação gradual (alinhado com CAGR 6,8%)
    2026: 0.09,  // 9% - Aceleração
    2027: 0.09,  // 9% - Manutenção
    2028: 0.08,  // 8% - Desaceleração
    2029: 0.07,  // 7% - Convergência para CAGR
    perpetuo: 0.03,  // 3% - Taxa perpetuidade
  },
  margemEBITDA: {
    2025: 0.13,  // 13% - +1.8pp vs 2024 (recuperação inicial)
    2026: 0.15,  // 15% - +2pp (ajuste estrutural)
    2027: 0.18,  // 18% - +3pp (volta aos patamares 2020-2021)
    2028: 0.20,  // 20% - +2pp (melhoria operacional)
    2029: 0.22,  // 22% - +2pp (patamar sustentável, 10pp abaixo do pico)
  },
  depreciacao_pct_receita: 0.03, // 3% ✅ ESPECÍFICO
  capex_pct_receita: {
    2025: 0.05, // 5% - Cenário Base: investimento moderado
    2026: 0.05,
    2027: 0.05,
    2028: 0.05,
    2029: 0.05,
  },
  nwc_pct_delta_receita: {
    2025: 0.035, // 3,5% - Cenário Base: gestão saudável de capital de giro
    2026: 0.035,
    2027: 0.035,
    2028: 0.035,
    2029: 0.035,
  },
  taxaImpostoEBIT: 0.18, // 18%
  beta: 0.95,
  wacc: 0.145, // 14,5% ✅ ESPECÍFICO
  g: 0.03, // 3%
  kpd_in_wacc: false,
};

/**
 * PREMISSAS 555 STUDIOS - CENÁRIO BASE (Crescimento Acelerado)
 * 
 * ⚠️ ATUALIZADO PÓS-NORMALIZAÇÃO DRE (14/10/2025)
 * 
 * CONTEXTO HISTÓRICO:
 * - Crescimento 2023→2024: +180,5% (hipercrescimento)
 * - Margem EBITDA Histórica: 36-41%
 * - Receita 2023: R$ 1,13M → 2024: R$ 3,18M
 * 
 * PREMISSAS DO CENÁRIO BASE:
 * - Crescimento acelerado nos primeiros anos (20-25%)
 * - Desaceleração gradual para convergência (10-12%)
 * - Margem EBITDA elevada e sustentável (35-38%)
 */
export const DEFAULT_555_ASSUMPTIONS: CompanyAssumptions = {
  nome: '555 Studios' as const,
  baseYear: 2024,         // ✅ NOVO: Último ano histórico (DRE vai até 2024)
  receitaBase: 3176912,   // 2024
  ebitdaBase: 1143645,    // 2024 (margem 36,0%)
  crescimento: {
    2025: 0.20,  // 20% - Continuação do hipercrescimento (desaceleração vs +180%)
    2026: 0.18,  // 18% - Desaceleração gradual
    2027: 0.15,  // 15% - Convergência
    2028: 0.12,  // 12% - Estabilização
    2029: 0.10,  // 10% - Maturidade relativa
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  margemEBITDA: {
    2025: 0.35,  // 35% - Leve compressão vs 36% (crescimento + investimentos)
    2026: 0.36,  // 36% - Recuperação com escala
    2027: 0.37,  // 37% - Ganhos de eficiência
    2028: 0.38,  // 38% - Patamar sustentável
    2029: 0.38,  // 38% - Estabilização
  },
  depreciacao_pct_receita: 0.02, // 2% ✅ ESPECÍFICO
  capex_pct_receita: {
    2025: 0.20, // 20% - Cenário Base: alto investimento em tech/crescimento
    2026: 0.18, // 18% - Redução gradual com escala
    2027: 0.16, // 16% - Ganhos de eficiência
    2028: 0.15, // 15% - Estabilização
    2029: 0.15, // 15% - Manutenção
  },
  nwc_pct_delta_receita: {
    2025: 0.06, // 6% - Cenário Base: gestão de capital de giro tech
    2026: 0.06,
    2027: 0.05, // 5% - Melhoria com escala
    2028: 0.05,
    2029: 0.05,
  },
  taxaImpostoEBIT: 0.18, // 18%
  beta: 1.05,
  wacc: 0.165, // 16,5% ✅ ESPECÍFICO
  g: 0.03, // 3%
  kpd_in_wacc: false,
};

// ============================================================================
// CENÁRIOS MÚLTIPLOS - SÉTIMA (Baseados em análise DRE 2020-2024)
// ============================================================================

/**
 * CENÁRIO PESSIMISTA - Sétima
 * Premissa: Crise de 2023 persiste; estrutura de custos não se ajusta
 */
export const SETIMA_CENARIO_PESSIMISTA = {
  crescimento: {
    2025: 0.05,  // 5% - Crescimento muito fraco
    2026: 0.05,  // 5% - Estagnação
    2027: 0.04,  // 4% - Desaceleração
    2028: 0.04,  // 4% - Baixo crescimento
    2029: 0.03,  // 3% - Convergência para inflação
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  margemBruta: {
    2025: 0.58, // 58% - Cenário Pessimista: margens comprimidas
    2026: 0.58,
    2027: 0.59,
    2028: 0.59,
    2029: 0.60,
  },
  margemEBITDA: {
    2025: 0.12,  // 12% - Margem estagnada
    2026: 0.13,  // 13% - Pequena melhora
    2027: 0.14,  // 14% - Recuperação lenta
    2028: 0.15,  // 15% - Abaixo da média histórica
    2029: 0.16,  // 16% - Muito abaixo do pico (32%)
  },
  margemLiquida: {
    2025: 0.06, // 6% - Cenário Pessimista: rentabilidade baixa
    2026: 0.065,
    2027: 0.07,
    2028: 0.075,
    2029: 0.08,
  },
};

/**
 * CENÁRIO BASE - Sétima (DEFAULT)
 * Premissa: Recuperação gradual; volta aos patamares 2020-2021
 */
export const SETIMA_CENARIO_BASE = {
  crescimento: DEFAULT_SETIMA_ASSUMPTIONS.crescimento,
  margemBruta: {
    2025: 0.62, // 62% - Cenário Base: tendência de melhora
    2026: 0.63,
    2027: 0.64,
    2028: 0.65,
    2029: 0.65,
  },
  margemEBITDA: DEFAULT_SETIMA_ASSUMPTIONS.margemEBITDA,
  margemLiquida: {
    2025: 0.095, // 9,5% - Cenário Base: melhora gradual
    2026: 0.10,
    2027: 0.105,
    2028: 0.11,
    2029: 0.11,
  },
};

/**
 * CENÁRIO OTIMISTA - Sétima
 * Premissa: Recuperação acelerada; volta próximo ao pico 2022
 */
export const SETIMA_CENARIO_OTIMISTA = {
  crescimento: {
    2025: 0.12,  // 12% - Recuperação forte
    2026: 0.12,  // 12% - Crescimento acelerado
    2027: 0.10,  // 10% - Sustentação
    2028: 0.09,  // 9% - Desaceleração controlada
    2029: 0.08,  // 8% - Convergência para CAGR+
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  margemBruta: {
    2025: 0.66, // 66% - Cenário Otimista: margens elevadas
    2026: 0.67,
    2027: 0.68,
    2028: 0.69,
    2029: 0.70,
  },
  margemEBITDA: {
    2025: 0.15,  // 15% - Salto inicial
    2026: 0.20,  // 20% - Reestruturação bem-sucedida
    2027: 0.25,  // 25% - Eficiência operacional
    2028: 0.28,  // 28% - Próximo ao pico
    2029: 0.30,  // 30% - Patamar elevado sustentável
  },
  margemLiquida: {
    2025: 0.12, // 12% - Cenário Otimista: rentabilidade alta
    2026: 0.14,
    2027: 0.16,
    2028: 0.17,
    2029: 0.18,
  },
};

// ============================================================================
// CENÁRIOS MÚLTIPLOS - 555 STUDIOS
// ============================================================================

/**
 * CENÁRIO PESSIMISTA - 555 Studios
 * Premissa: Desaceleração brusca; perda de competitividade
 */
export const STUDIOS555_CENARIO_PESSIMISTA = {
  crescimento: {
    2025: 0.10,  // 10% - Forte desaceleração vs +180%
    2026: 0.08,  // 8% - Perda de momentum
    2027: 0.07,  // 7% - Estagnação
    2028: 0.06,  // 6% - Baixo crescimento
    2029: 0.05,  // 5% - Maturidade precoce
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  margemBruta: {
    2025: 0.60, // 60% - Cenário Pessimista 555: margens comprimidas
    2026: 0.59,
    2027: 0.58,
    2028: 0.58,
    2029: 0.57,
  },
  margemEBITDA: {
    2025: 0.30,  // 30% - Compressão de margem
    2026: 0.29,  // 29% - Pressão competitiva
    2027: 0.28,  // 28% - Deterioração
    2028: 0.28,  // 28% - Estabilização baixa
    2029: 0.27,  // 27% - Abaixo da média histórica
  },
  margemLiquida: {
    2025: 0.18, // 18% - Cenário Pessimista 555: rentabilidade em queda
    2026: 0.17,
    2027: 0.16,
    2028: 0.15,
    2029: 0.15,
  },
};

/**
 * CENÁRIO BASE - 555 Studios (DEFAULT)
 * Premissa: Crescimento acelerado com desaceleração gradual
 */
export const STUDIOS555_CENARIO_BASE = {
  crescimento: DEFAULT_555_ASSUMPTIONS.crescimento,
  margemBruta: {
    2025: 0.65, // 65% - Cenário Base 555: modelo escalável
    2026: 0.66,
    2027: 0.67,
    2028: 0.68,
    2029: 0.68,
  },
  margemEBITDA: DEFAULT_555_ASSUMPTIONS.margemEBITDA,
  margemLiquida: {
    2025: 0.25, // 25% - Cenário Base 555: rentabilidade elevada
    2026: 0.26,
    2027: 0.27,
    2028: 0.28,
    2029: 0.29,
  },
};

/**
 * CENÁRIO OTIMISTA - 555 Studios
 * Premissa: Hipercrescimento sustentado; liderança de mercado
 */
export const STUDIOS555_CENARIO_OTIMISTA = {
  crescimento: {
    2025: 0.30,  // 30% - Continuação do hipercrescimento
    2026: 0.25,  // 25% - Crescimento elevado
    2027: 0.20,  // 20% - Desaceleração gradual
    2028: 0.15,  // 15% - Estabilização
    2029: 0.12,  // 12% - Maturidade relativa
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  margemBruta: {
    2025: 0.70, // 70% - Cenário Otimista 555: margens premium
    2026: 0.71,
    2027: 0.72,
    2028: 0.73,
    2029: 0.74,
  },
  margemEBITDA: {
    2025: 0.38,  // 38% - Ganhos de escala
    2026: 0.39,  // 39% - Eficiência operacional
    2027: 0.40,  // 40% - Liderança de mercado
    2028: 0.41,  // 41% - Patamar premium (histórico)
    2029: 0.41,  // 41% - Sustentação
  },
  margemLiquida: {
    2025: 0.30, // 30% - Cenário Otimista 555: rentabilidade excepcional
    2026: 0.32,
    2027: 0.34,
    2028: 0.35,
    2029: 0.36,
  },
};

// ============================================================================
// CONTEXTO HISTÓRICO - OUTLIERS DOCUMENTADOS
// ============================================================================

/**
 * Outlier documentado: Crise Sétima 2023
 * 
 * CAUSA: Queda de 31% na receita e colapso de margem para 1,1%
 * IMPACTO: 2023 não deve ser usado como ano base sem ajustes
 */
export const SETIMA_OUTLIER_2023 = {
  ano: 2023,
  tipo: 'colapso' as const,
  margemReal: 0.011,      // 1,1%
  margemEsperada: 0.25,   // ~25% (média 2020-2022)
  desvio: -0.239,         // -23,9 pontos percentuais
  causa: 'Queda de receita de -31,2% + descontrole de custos operacionais (54% da receita)',
  impactoNoModelo: 'Ano base ajustado para 2024; premissas de recuperação gradual até 2029',
};

